import { useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { modules, SECTIONS, type NavItem } from "./modules";
import { useOrg, useRestricted } from "./org";
import { useSession, ROLE_LABELS } from "./session";
import { Loading, Logo } from "./ui";

const CORE_NAV: NavItem[] = [
  { to: "parametres/membres", label: "Membres et droits", section: "Espace", perm: "read", order: 90 },
  { to: "parametres/envois", label: "Envois", section: "Espace", perm: "read", order: 91, hideWhenRestricted: true },
  { to: "parametres/services", label: "Services tiers", section: "Espace", perm: "members", order: 92 },
  { to: "parametres/journal", label: "Journal", section: "Espace", perm: "members", order: 93 },
];

/** Exige une session ; sinon renvoie vers la connexion en gardant la destination. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to={`/connexion?suite=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

export function AppShell() {
  const { user, memberships, logout } = useSession();
  const org = useOrg();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  // Collaborateur au périmètre restreint (F-091) : les rubriques que le serveur lui refuse sont masquées.
  const restricted = useRestricted();
  if (!org.role) {
    return (
      <div className="public-shell">
        <div className="public-card">
          <h1>Espace introuvable</h1>
          <p>Cet espace n’existe pas ou vous n’y avez pas accès.</p>
          <Link to="/espaces" className="bv-btn bv-btn-primaire">
            Voir mes espaces
          </Link>
        </div>
      </div>
    );
  }
  const items = [...modules.flatMap((m) => m.nav ?? []), ...CORE_NAV].filter((i) => (!i.perm || org.can(i.perm)) && !(restricted && i.hideWhenRestricted));
  return (
    <div className={`app ${open ? "nav-open" : ""}`}>
      <a className="skip" href="#contenu">
        Aller au contenu
      </a>
      <aside className="sidebar" aria-label="Navigation principale">
        <div className="sidebar-top">
          <Link to={org.to()} className="sidebar-logo" onClick={() => setOpen(false)}>
            <Logo inverse height={26} />
          </Link>
          <button type="button" className="menu-btn" aria-expanded={open} onClick={() => setOpen(!open)}>
            Menu
          </button>
        </div>
        <label className="org-switch">
          <span className="sr-only">Espace</span>
          <select
            value={org.orgId}
            onChange={(e) => {
              if (e.target.value === "__nouveau") nav("/espaces?nouveau=1");
              else nav(`/espace/${e.target.value}`);
            }}
          >
            {memberships.map((m) => (
              <option key={m.org_id} value={m.org_id}>
                {m.org_name}
              </option>
            ))}
            <option value="__nouveau">+ Nouvel espace</option>
          </select>
        </label>
        <nav className="nav">
          <NavLink to={org.to()} end className="nav-link" onClick={() => setOpen(false)}>
            Tableau de bord
          </NavLink>
          {SECTIONS.map((section) => {
            const list = items.filter((i) => i.section === section).sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
            if (!list.length) return null;
            return (
              <div key={section} className="nav-section">
                <p className="nav-caption">{section}</p>
                {list.map((i) => (
                  <NavLink key={i.to} to={org.to(i.to)} className="nav-link" onClick={() => setOpen(false)}>
                    {i.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-foot">
          <Link to="/moi" className="nav-link small">
            {user?.full_name}
            <span className="block muted-inverse">{org.role ? ROLE_LABELS[org.role] : ""}</span>
          </Link>
          <button
            type="button"
            className="link-inverse"
            onClick={async () => {
              await logout();
              nav("/connexion");
            }}
          >
            Se déconnecter
          </button>
        </div>
      </aside>
      <main id="contenu" className="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

/** Gabarit des pages publiques et de compte (connexion, candidature, espace personnel). */
export function PublicShell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const { user } = useSession();
  return (
    <div className="public-shell">
      <header className="public-top">
        <Link to="/">
          <Logo height={28} />
        </Link>
        {user ? (
          <Link to="/moi" className="small">
            {user.full_name}
          </Link>
        ) : (
          <Link to="/connexion" className="small">
            Se connecter
          </Link>
        )}
      </header>
      <div className={wide ? "public-wide" : "public-card"}>{children}</div>
      <footer className="public-foot small muted">Bailivo · Du premier dossier au dernier loyer.</footer>
    </div>
  );
}
